"use client";

import clsx from "clsx";
import {Fragment, useEffect, useRef} from "react";
import Icon, {ICON_TYPE} from "@/components/library/Icon";

export interface StepperStep {
   key: string;
   label: string;
   number: number;
}

interface StepperProps {
   steps: StepperStep[];
   currentStep: string;
   onStepClick?: (key: string) => void;
   allowNavigation?: boolean;
   compact?: boolean;
   flush?: boolean;
   /**
    * `dots` renders numbered circles joined by lines, with a check on steps
    * that have been reached.
    */
   variant?: "default" | "dots";
   /** Furthest step reached. Steps after it are locked. Defaults to every step unlocked. */
   reachedStep?: string;
}

export default function Stepper({
   steps,
   currentStep,
   onStepClick,
   allowNavigation = true,
   compact = false,
   flush = false,
   variant = "default",
   reachedStep,
}: Readonly<StepperProps>) {
   const stepRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
   const reachedIndex = reachedStep
      ? steps.findIndex((step) => step.key === reachedStep)
      : steps.length - 1;

   useEffect(() => {
      const el = stepRefs.current.get(currentStep);
      if (el) {
         el.scrollIntoView?.({behavior: "smooth", inline: "center", block: "nearest"});
      }
   }, [currentStep]);

   const setStepRef = (key: string) => (node: HTMLButtonElement | null) => {
      if (node) {
         stepRefs.current.set(key, node);
      } else {
         stepRefs.current.delete(key);
      }
   };

   if (variant === "dots") {
      return (
         <div className={clsx("flex items-center min-w-0 gap-2", !flush && "py-4")}>
            {steps.map((step, index) => {
               const isCurrent = step.key === currentStep;
               const isReached = index <= reachedIndex;
               const isDone = isReached && !isCurrent;
               const isClickable = allowNavigation && !!onStepClick && isDone;

               return (
                  <Fragment key={step.key}>
                     <button
                        ref={setStepRef(step.key)}
                        type="button"
                        disabled={!isClickable}
                        aria-current={isCurrent ? "step" : undefined}
                        onClick={() => isClickable && onStepClick?.(step.key)}
                        className={clsx(
                           "flex items-center gap-2 text-xs font-medium whitespace-nowrap",
                           isReached ? "text-1000" : "text-500",
                           isClickable ? "cursor-pointer hover:text-primary" : "cursor-default"
                        )}
                     >
                        <span
                           className={clsx(
                              "flex items-center justify-center size-5 rounded-full border transition-colors",
                              isCurrent && "bg-primary border-primary text-white",
                              isDone && "bg-primary/5 border-primary/30 text-primary",
                              !isReached && "bg-panel border-300 text-500"
                           )}
                        >
                           {isDone ? (
                              <Icon icon="check" className="size-2.5" />
                           ) : (
                              step.number
                           )}
                        </span>
                        {step.label}
                     </button>
                     {index < steps.length - 1 && (
                        <span
                           aria-hidden
                           className={clsx(
                              "h-px w-5 shrink-0 transition-colors",
                              index < reachedIndex ? "bg-primary" : "bg-300"
                           )}
                        />
                     )}
                  </Fragment>
               );
            })}
         </div>
      );
   }

   return (
      <div
         className={clsx(
            "flex items-center min-w-0 overflow-x-auto scrollbar-hide of-right of-left",
            !flush && "py-4",
            !compact && "w-full"
         )}
         style={{scrollbarWidth: "none", msOverflowStyle: "none"}}
      >
         {steps.map((step, index) => {
            const isCurrent = step.key === currentStep;
            const isClickable = allowNavigation && !!onStepClick && index <= reachedIndex;

            return (
               <Fragment key={step.key}>
                  <button
                     ref={setStepRef(step.key)}
                     type="button"
                     disabled={!isClickable}
                     onClick={() => isClickable && onStepClick?.(step.key)}
                     className={clsx(
                        "text-sm font-medium transition-colors whitespace-nowrap rounded-lg px-3 py-1.5",
                        isCurrent && "text-primary bg-primary/10",
                        !isCurrent && isClickable && "text-600 hover:text-primary cursor-pointer",
                        !isCurrent && !isClickable && "text-400 cursor-default"
                     )}
                  >
                     {step.number}. {step.label}
                  </button>
                  {index < steps.length - 1 && (
                     <div className={clsx(
                        "flex items-center justify-center min-w-6",
                        compact ? "px-2" : "flex-1"
                     )}>
                        <Icon
                           icon="arrow-right"
                           className="text-xs text-500"
                           type={ICON_TYPE.SOLID}
                        />
                     </div>
                  )}
               </Fragment>
            );
         })}
      </div>
   );
}
